import * as THREE from 'three';
import type { AudioEngine } from '../audio/AudioEngine';
import { plotPosition } from '../game/grid';
import { resolvePlotIntent, type IntentTone, type ToolAnimation } from '../game/interaction';
import { moveSpeedMultiplier } from '../game/upgrades';
import type { InputManager, PointerClick } from '../input/InputManager';
import type { PlayerInfo, PlayerPose } from '../net/protocol';
import { Character } from '../render/Character';
import { PROP_STANDS, type PropId } from '../render/FarmProps';
import { BOUNDS, LAYOUT, STATIC_OBSTACLES } from '../render/layout';
import type { WorldRenderer } from '../render/WorldRenderer';
import { openPanel, toast, ui, type PanelId } from '../ui/store';
import { angleDelta, clamp, damp } from '../utils/math';
import type { GameController } from './GameController';

const BASE_SPEED = 6;
const PLAYER_RADIUS = 0.35;
/** How close (to a plot centre) the farmer must be to tend it. */
const PLOT_REACH = 2.5;
const PROP_REACH = 1.0;

const PROP_PANELS: Record<PropId, PanelId> = { market: 'market', board: 'orders', house: 'house' };
const PROP_LABELS: Record<PropId, string> = {
  market: '🏪 Market stand — sell flowers & buy seeds',
  board: '📜 Order board',
  house: '🏡 Your cottage',
};
const PROP_POSITIONS: Record<PropId, { x: number; z: number }> = {
  market: LAYOUT.market,
  board: LAYOUT.board,
  house: LAYOUT.house,
};

interface WalkTarget {
  point: THREE.Vector3;
  reach: number;
  onArrive: () => void;
  lastDistance: number;
  stuck: number;
}

interface RemotePlayer {
  info: PlayerInfo;
  character: Character;
  target: THREE.Vector3;
  rotation: number;
  moving: boolean;
  initialized: boolean;
}

const _move = new THREE.Vector3();
const _before = new THREE.Vector3();

/**
 * Turns input into farmer movement and farm interactions, and keeps remote
 * visitors' characters in step with their network poses.
 */
export class PlayController {
  readonly position = new THREE.Vector3(LAYOUT.spawn.x, 0, LAYOUT.spawn.z);
  readonly character: Character;
  rotation = Math.PI;

  private speed = 0;
  private time = 0;
  private target: WalkTarget | null = null;
  private pendingAnim: ToolAnimation | undefined;
  private readonly remotes = new Map<string, RemotePlayer>();
  private readonly marker: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  private markerTime = 0;
  private hoverKey = '';

  constructor(
    private readonly game: GameController,
    private readonly world: WorldRenderer,
    private readonly input: InputManager,
    private readonly audio: AudioEngine,
    color: string,
  ) {
    this.character = new Character(color);
    world.scene.add(this.character.root);

    this.marker = new THREE.Mesh(
      new THREE.RingGeometry(0.25, 0.4, 24).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    );
    this.marker.position.y = 0.06;
    world.scene.add(this.marker);

    input.clicks.on((click) => this.onClick(click));
  }

  setIdentity(name: string, color: string, showName: boolean): void {
    this.character.setColor(color);
    this.character.setName(showName ? name : null);
  }

  /** Pose for the network, including any one-shot animation started since last call. */
  pose(): PlayerPose {
    const pose: PlayerPose = {
      x: this.position.x,
      z: this.position.z,
      rot: this.rotation,
      moving: this.speed > 0.1,
    };
    if (this.pendingAnim) pose.anim = this.pendingAnim;
    this.pendingAnim = undefined;
    return pose;
  }

  /** Distance-based volume so far-away visitors' actions sound quieter. */
  volumeAt(x: number, z: number): number {
    return clamp(1.2 - Math.hypot(x - this.position.x, z - this.position.z) / 35, 0.3, 1);
  }

  update(dt: number): void {
    this.time += dt;
    const orbit = this.input.consumeOrbit();
    this.world.rig.rotate(orbit.x + this.input.keyOrbit() * dt * 1.8, orbit.y);
    if (orbit.zoom !== 1) this.world.rig.zoom(orbit.zoom);

    this.updateMovement(dt);
    this.character.root.position.copy(this.position);
    this.character.root.rotation.y = this.rotation;
    this.character.setBoots(this.game.state.upgrades.boots);
    this.character.update(dt, this.speed, this.time);

    for (const remote of this.remotes.values()) {
      const root = remote.character.root;
      if (!remote.initialized) {
        root.position.copy(remote.target);
        remote.initialized = true;
      }
      const before = _before.copy(root.position);
      root.position.lerp(remote.target, damp(10, dt));
      root.rotation.y += angleDelta(root.rotation.y, remote.rotation) * damp(10, dt);
      const moved = before.distanceTo(root.position) / Math.max(dt, 1e-3);
      remote.character.update(dt, remote.moving ? clamp(moved / BASE_SPEED, 0.3, 1) : 0, this.time);
    }

    this.markerTime = Math.max(0, this.markerTime - dt);
    this.marker.material.opacity = this.markerTime * 0.8;
    this.marker.scale.setScalar(1 + (1 - this.markerTime) * 0.6);

    this.updateHover();
  }

  /** Walks to a prop and opens its panel (used by HUD buttons too). */
  useProp(prop: PropId): void {
    const stand = PROP_STANDS[prop];
    this.walkTo(stand.x, stand.z, PROP_REACH, () => {
      const p = PROP_POSITIONS[prop];
      this.face(p.x, p.z);
      openPanel(PROP_PANELS[prop]);
      this.audio.play('pop');
    });
  }

  /** Keyboard "act": tend the plot in front of the farmer, or use a nearby prop. */
  act(): void {
    const front = new THREE.Vector3(Math.sin(this.rotation), 0, Math.cos(this.rotation))
      .multiplyScalar(1.3)
      .add(this.position);
    let best: { id: number; d: number } | null = null;
    for (let id = 0; id < this.game.state.plots.length; id++) {
      const { x, z } = plotPosition(id);
      const d = Math.hypot(x - front.x, z - front.z);
      if (d < 1.7 && (!best || d < best.d)) best = { id, d };
    }
    if (best) {
      this.usePlot(best.id);
      return;
    }
    for (const prop of Object.keys(PROP_STANDS) as PropId[]) {
      const stand = PROP_STANDS[prop];
      if (Math.hypot(stand.x - this.position.x, stand.z - this.position.z) < 3.5) {
        this.useProp(prop);
        return;
      }
    }
  }

  usePlot(plotId: number): void {
    const intent = resolvePlotIntent(this.game.state, plotId, ui.tool.value, ui.seed.value);
    if (!intent.action) {
      if (intent.tone === 'blocked') {
        toast(intent.label, 'bad');
        this.audio.play('error');
      }
      return;
    }
    const { x, z } = plotPosition(plotId);
    this.walkTo(x, z, PLOT_REACH, () => {
      // Re-resolve on arrival: the plot may have changed while we walked.
      const fresh = resolvePlotIntent(this.game.state, plotId, ui.tool.value, ui.seed.value);
      if (!fresh.action) return;
      this.face(x, z);
      this.character.playAction(fresh.animation);
      this.pendingAnim = fresh.animation;
      this.world.plots.bump(plotId, 0.35);
      this.game.dispatch(fresh.action);
    });
  }

  // ---------------------------------------------------------------------------
  // Remote players
  // ---------------------------------------------------------------------------

  setPlayers(players: PlayerInfo[], localId: string): void {
    const ids = new Set(players.map((p) => p.id));
    for (const [id, remote] of this.remotes) {
      if (!ids.has(id) || id === localId) {
        remote.character.dispose();
        this.remotes.delete(id);
      }
    }
    for (const info of players) {
      if (info.id === localId) continue;
      const existing = this.remotes.get(info.id);
      if (existing) {
        existing.info = info;
        existing.character.setColor(info.color);
        existing.character.setName(info.name);
        continue;
      }
      const character = new Character(info.color, info.name);
      this.world.scene.add(character.root);
      this.remotes.set(info.id, {
        info,
        character,
        target: new THREE.Vector3(LAYOUT.spawn.x, 0, LAYOUT.spawn.z),
        rotation: Math.PI,
        moving: false,
        initialized: false,
      });
    }
  }

  setRemotePose(id: string, pose: PlayerPose): void {
    const remote = this.remotes.get(id);
    if (!remote) return;
    remote.target.set(pose.x, 0, pose.z);
    remote.rotation = pose.rot;
    remote.moving = pose.moving;
    if (pose.anim) remote.character.playAction(pose.anim);
  }

  remotePosition(id: string): THREE.Vector3 | null {
    return this.remotes.get(id)?.character.root.position ?? null;
  }

  say(id: string | null, text: string): void {
    if (id === null) this.character.say(text);
    else this.remotes.get(id)?.character.say(text);
  }

  clearRemotes(): void {
    for (const remote of this.remotes.values()) remote.character.dispose();
    this.remotes.clear();
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private onClick(click: PointerClick): void {
    if (!ui.started.value) return;
    const pick = this.world.pick(click.ndcX, click.ndcY);
    if (!pick) return;
    if (pick.kind === 'plot') this.usePlot(pick.plotId);
    else if (pick.kind === 'prop') this.useProp(pick.prop);
    else {
      const x = clamp(pick.point.x, BOUNDS.minX, BOUNDS.maxX);
      const z = clamp(pick.point.z, BOUNDS.minZ, BOUNDS.maxZ);
      this.marker.position.set(x, 0.06, z);
      this.markerTime = 1;
      this.walkTo(x, z, 0.2, () => undefined);
    }
  }

  private walkTo(x: number, z: number, reach: number, onArrive: () => void): void {
    const point = new THREE.Vector3(x, 0, z);
    const distance = Math.hypot(x - this.position.x, z - this.position.z);
    if (distance <= reach) {
      this.target = null;
      onArrive();
      return;
    }
    this.target = { point, reach, onArrive, lastDistance: distance, stuck: 0 };
  }

  private face(x: number, z: number): void {
    const dx = x - this.position.x;
    const dz = z - this.position.z;
    if (dx * dx + dz * dz > 0.01) this.rotation = Math.atan2(dx, dz);
  }

  private updateMovement(dt: number): void {
    const axis = this.input.moveAxis();
    const maxSpeed = BASE_SPEED * moveSpeedMultiplier(this.game.state.upgrades.boots);
    _move.set(0, 0, 0);

    if (axis.x !== 0 || axis.y !== 0) {
      this.target = null;
      this.world.rig.screenToWorld(axis.x, axis.y, _move);
    } else if (this.target) {
      const target = this.target;
      const distance = Math.hypot(
        target.point.x - this.position.x,
        target.point.z - this.position.z,
      );
      if (distance <= target.reach) {
        this.target = null;
        target.onArrive();
      } else {
        _move
          .set(target.point.x - this.position.x, 0, target.point.z - this.position.z)
          .normalize();
        // Give up if an obstacle keeps us from making progress.
        target.stuck =
          target.lastDistance - distance < maxSpeed * dt * 0.25 ? target.stuck + dt : 0;
        target.lastDistance = distance;
        if (target.stuck > 0.6) this.target = null;
      }
    }

    if (_move.lengthSq() > 0) {
      this.position.addScaledVector(_move, maxSpeed * dt);
      this.resolveCollisions();
      this.rotation += angleDelta(this.rotation, Math.atan2(_move.x, _move.z)) * damp(14, dt);
      this.speed += (1 - this.speed) * damp(10, dt);
    } else {
      this.speed += (0 - this.speed) * damp(12, dt);
    }
  }

  private resolveCollisions(): void {
    for (const o of STATIC_OBSTACLES) {
      const dx = this.position.x - o.x;
      const dz = this.position.z - o.z;
      const min = o.r + PLAYER_RADIUS;
      const d = Math.hypot(dx, dz);
      if (d < min && d > 1e-4) {
        this.position.x = o.x + (dx / d) * min;
        this.position.z = o.z + (dz / d) * min;
      }
    }
    this.position.x = clamp(this.position.x, BOUNDS.minX, BOUNDS.maxX);
    this.position.z = clamp(this.position.z, BOUNDS.minZ, BOUNDS.maxZ);
  }

  private updateHover(): void {
    const hover = this.input.hover;
    const canvas = this.world.canvas;
    if (!hover || !ui.started.value || ui.panel.value !== null) {
      this.clearHover();
      return;
    }
    const pick = this.world.pick(hover.ndcX, hover.ndcY);
    if (pick?.kind === 'plot') {
      const intent = resolvePlotIntent(this.game.state, pick.plotId, ui.tool.value, ui.seed.value);
      this.world.plots.setHover(pick.plotId, intent.tone);
      this.setHoverLabel(hover.clientX, hover.clientY, intent.label, intent.tone);
      canvas.style.cursor = intent.action ? 'pointer' : 'default';
    } else if (pick?.kind === 'prop') {
      this.world.plots.setHover(null);
      this.setHoverLabel(hover.clientX, hover.clientY, PROP_LABELS[pick.prop], 'info');
      canvas.style.cursor = 'pointer';
    } else {
      this.clearHover();
    }
  }

  private setHoverLabel(x: number, y: number, label: string, tone: IntentTone): void {
    const key = `${Math.round(x)}:${Math.round(y)}:${label}:${tone}`;
    if (key === this.hoverKey) return;
    this.hoverKey = key;
    ui.hover.value = label ? { x, y, label, tone } : null;
  }

  private clearHover(): void {
    this.world.plots.setHover(null);
    this.world.canvas.style.cursor = 'default';
    if (this.hoverKey !== '') {
      this.hoverKey = '';
      ui.hover.value = null;
    }
  }
}
